import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Box, IconButton, Modal, Skeleton, Typography } from '@mui/material';
import { DismissIcon } from '@wavetrak/theme';
import CloudflareResizedImage from 'components/CloudflareResizedImage';
import useCameraMatchConditions from 'hooks/useCameraMatchConditions';
import type { CamForecastComparison, CamForecastTopMatch } from 'types/camera';

import { CamMatchesForecastComparisonCards } from './CamMatchesForecastComparisonCards';
import { camMatchTimestampSeconds } from './formatCamMatchConditions';
import { formatCamMatchDateTime, formatForecastComparisonLabel } from './formatCamMatchDisplay';
import {
  CAM_MATCH_MODAL_IMAGE_HEIGHT,
  CAM_MATCH_MODAL_IMAGE_QUALITY,
  CAM_MATCH_MODAL_IMAGE_WIDTH,
  CAM_MATCH_MODAL_TABLET_LG,
} from './prefetchCamMatchThumbnails';
import styles from './CamMatchesForecastModal.module.scss';

const MODAL_IMAGE_SIZES = `(max-width: ${CAM_MATCH_MODAL_TABLET_LG}px) 100vw, ${CAM_MATCH_MODAL_IMAGE_WIDTH}px`;

export interface CamMatchesForecastModalProps {
  open: boolean;
  onClose: () => void;
  spotId: string;
  match: CamForecastTopMatch;
  forecastComparison: Pick<CamForecastComparison, 'datetime' | 'utcOffset'> | null;
}

export const CamMatchesForecastModal: React.FC<CamMatchesForecastModalProps> = ({
  open,
  onClose,
  spotId,
  match,
  forecastComparison,
}) => {
  const images = match.multipleThumbnailUrls.filter(Boolean);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [isCarouselImageLoading, setIsCarouselImageLoading] = useState(true);

  const activeImage = images[activePageIndex];
  const hasPrevPage = activePageIndex > 0;
  const hasNextPage = activePageIndex < images.length - 1;

  const camTimestampSeconds = useMemo(
    () => camMatchTimestampSeconds(match.localTimestamp),
    [match.localTimestamp],
  );
  const forecastTimestampSeconds = forecastComparison?.datetime ?? null;

  const { cam, forecast, isLoading } = useCameraMatchConditions({
    spotId,
    enabled: open,
    camTimestampSeconds,
    forecastTimestampSeconds,
  });

  useEffect(() => {
    if (!open) return;
    setActivePageIndex(0);
    setIsCarouselImageLoading(true);
  }, [open, match.exemplarId]);

  useEffect(() => {
    setIsCarouselImageLoading(true);
  }, [activePageIndex]);

  const handleCarouselImageLoaded = useCallback(() => {
    setIsCarouselImageLoading(false);
  }, []);

  const handleCarouselImageError = useCallback(() => {
    setIsCarouselImageLoading(false);
  }, []);

  const camLabel = formatCamMatchDateTime(match.localTimestamp);
  const forecastLabel =
    forecastComparison != null
      ? formatForecastComparisonLabel(forecastComparison.datetime, forecastComparison.utcOffset)
      : '—';

  const overall = Math.round(match.overallSimilarity);
  const units = cam?.units ?? forecast?.units;

  if (!images.length) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="cam-matches-forecast-modal-title"
      slotProps={{ backdrop: { 'aria-hidden': true } }}
    >
      <Box className={styles.centerWrap}>
        <Box
          className={styles.paper}
          data-testid="cam-matches-forecast-modal"
          role="dialog"
          aria-modal="true"
        >
          <IconButton
            type="button"
            className={styles.dismiss}
            data-testid="cam-matches-forecast-modal-close"
            aria-label="Close forecast match"
            disableFocusRipple
            disableRipple
            disableTouchRipple
            onClick={onClose}
          >
            <DismissIcon />
          </IconButton>

          <Typography component="h2" id="cam-matches-forecast-modal-title" className={styles.title}>
            Forecast match
          </Typography>

          <Box className={styles.body}>
            <Box className={styles.left}>
              <Typography component="span" className={styles.srOnly}>
                Image {activePageIndex + 1} of {images.length}
              </Typography>

              <Box className={styles.carousel} aria-busy={isCarouselImageLoading}>
                <Box className={styles.carouselImageWrap}>
                  {isCarouselImageLoading ? (
                    <Skeleton
                      className={styles.carouselImageSkeleton}
                      variant="rectangular"
                      animation="wave"
                      width="100%"
                      height="100%"
                      aria-hidden
                      data-testid="cam-matches-forecast-modal-carousel-skeleton"
                    />
                  ) : null}
                  <Box
                    className={classNames(
                      styles.carouselImage,
                      isCarouselImageLoading && styles.carouselImageHidden,
                    )}
                    data-testid="cam-matches-forecast-modal-carousel-image"
                    data-loading={isCarouselImageLoading ? 'true' : 'false'}
                  >
                    <CloudflareResizedImage
                      key={activeImage}
                      src={activeImage}
                      alt={`Cam match still ${activePageIndex + 1} of ${images.length}`}
                      width={CAM_MATCH_MODAL_IMAGE_WIDTH}
                      height={CAM_MATCH_MODAL_IMAGE_HEIGHT}
                      sizes={MODAL_IMAGE_SIZES}
                      layout="fill"
                      loading="eager"
                      objectFit="cover"
                      objectPosition="center"
                      quality={CAM_MATCH_MODAL_IMAGE_QUALITY}
                      cloudflareOptions={{ fit: 'cover' }}
                      onLoadingComplete={handleCarouselImageLoaded}
                      onError={handleCarouselImageError}
                    />
                  </Box>
                </Box>
                <Box aria-hidden className={styles.carouselGradient} />
                {images.length > 1 ? (
                  <>
                    <Box className={styles.carouselNav}>
                      <IconButton
                        type="button"
                        className={styles.carouselNavBtn}
                        aria-label="Previous image"
                        disabled={!hasPrevPage}
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        onClick={() => setActivePageIndex((index) => index - 1)}
                      >
                        <ChevronLeftRoundedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        type="button"
                        className={styles.carouselNavBtn}
                        aria-label="Next image"
                        disabled={!hasNextPage}
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        onClick={() => setActivePageIndex((index) => index + 1)}
                      >
                        <ChevronRightRoundedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box
                      aria-hidden
                      className={styles.carouselPagination}
                      data-testid="cam-matches-forecast-modal-pagination"
                    >
                      {images.map((_, dotIndex) => (
                        <Box
                          key={`dot-${dotIndex}`}
                          component="span"
                          className={classNames(
                            styles.carouselDot,
                            dotIndex === activePageIndex && styles.carouselDotActive,
                          )}
                        />
                      ))}
                    </Box>
                  </>
                ) : null}
              </Box>
            </Box>

            <Box className={styles.right}>
              <Box className={styles.headerRow}>
                <Box className={styles.headerCol}>
                  <Typography component="span" className={styles.headerLabel}>
                    Cam match
                  </Typography>
                  <Typography component="span" className={styles.headerValue}>
                    {camLabel || '—'}
                  </Typography>
                </Box>

                <Box className={styles.connector}>
                  <Box className={styles.connectorDot} aria-hidden />
                  <Box className={styles.connectorLine} aria-hidden />
                  <Box className={styles.connectorCenter}>
                    <Typography component="span" className={styles.connectorPct}>
                      {overall}%
                    </Typography>
                    <Typography component="span" className={styles.connectorMatchLabel}>
                      Match
                    </Typography>
                  </Box>
                  <Box className={styles.connectorLine} aria-hidden />
                  <Box className={styles.connectorDot} aria-hidden />
                </Box>

                <Box className={styles.headerColEnd}>
                  <Typography component="span" className={styles.headerLabel}>
                    Forecast
                  </Typography>
                  <Typography component="span" className={styles.headerValue}>
                    {forecastLabel}
                  </Typography>
                </Box>
              </Box>

              <CamMatchesForecastComparisonCards
                camConditions={cam?.data.conditions}
                forecastConditions={forecast?.data.conditions}
                units={units}
                swellSimilarity={match.swellSimilarity}
                windSimilarity={match.windSimilarity}
                tideSimilarity={match.tideSimilarity}
                isLoading={isLoading}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
